import { OffsetMaterials, Materials } from '@consts/materials';
import { styled } from '@mui/material';
import { Box, Slider, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

const StyledSlider = styled(Slider)(({ theme }) => ({
    height: 8,
    padding: '16px 0',
    '& .MuiSlider-track': {
        border: 0,
        backgroundColor: theme.palette.primary.main,
    },
    '& .MuiSlider-rail': {
        opacity: 0.15,
        backgroundColor: theme.palette.grey[300],
    },
    '& .MuiSlider-thumb': {
        width: 18,
        height: 18,
        borderRadius: '50%',
        backgroundColor: theme.palette.common.white,
        border: `2px solid ${theme.palette.primary.main}`,
        '&:focus, &:hover, &.Mui-active': {
            boxShadow: 'inherit',
        },
    },
}));

const MODEL_STEPS: {
    productIds: number[];
    step: number;
    min: number;
    max: number;
    material: Partial<Record<Materials, { default: number }>>;
    range: string;
}[] = [
    {
        productIds: [4400],
        step: 1,
        min: -20,
        max: 20,
        material: {
            [Materials.Matte]: { default: 14 },
            [Materials.Original]: { default: 14 },
            [Materials.StyleSkins]: { default: 14 },
            [Materials.Privacy]: { default: 14 },
            [Materials.Fusion]: { default: 25 },
            [Materials.FusionPro]: { default: 21 },
        },
        range: '1-40',
    },
    {
        productIds: [67],
        step: 5,
        min: -200,
        max: 200,
        material: {
            [Materials.Matte]: { default: 250 },
            [Materials.Original]: { default: 250 },
            [Materials.StyleSkins]: { default: 250 },
            [Materials.Privacy]: { default: 250 },
            [Materials.Fusion]: { default: 310 },
            [Materials.FusionPro]: { default: 310 },
        },
        range: '1-1000',
    },
    {
        productIds: [10],
        step: 1,
        min: -40,
        max: 40,
        material: {
            [Materials.Matte]: { default: 36 },
            [Materials.Original]: { default: 36 },
            [Materials.StyleSkins]: { default: 36 },
            [Materials.Privacy]: { default: 36 },
            [Materials.Fusion]: { default: 50 },
            [Materials.FusionPro]: { default: 44 },
        },
        range: '1-100',
    },
];
type ForceMaterialsState = null | Record<
    string,
    {
        materials: Record<string, { forceOffset: number }>;
    }
>;

interface ForceMaterialsProps {
    productId: string;
    unlocked: boolean;
    state: ForceMaterialsState;
    dispatch: (value: ForceMaterialsState) => void;
}

export const ForceMaterials = ({ productId, unlocked, state, dispatch }: ForceMaterialsProps) => {
    const stepMap = useMemo(
        () => MODEL_STEPS.find((s) => s.productIds.includes(Number(productId))) ?? MODEL_STEPS[0],
        [productId]
    );

    /* ---------------------------------------------------------------------- */
    /*                       MATERIAL TAB / TOGGLE GROUP                      */
    /* ---------------------------------------------------------------------- */
    const materialEntries = useMemo(() => Object.entries(OffsetMaterials), []);
    const [selected, setSelected] = useState<number>(1);

    const forceOffset = (productId ? state?.[productId]?.materials[selected]?.forceOffset : 0) ?? 0;
    const defaultVal = (stepMap.material as any)[selected]?.default ?? 0;

    const handleSlider = (_: any, value: number | number[]) => {
        dispatch({
            ...state,
            [productId]: {
                materials: {
                    ...(state?.[productId]?.materials ?? {}),
                    [selected]: { forceOffset: value as number },
                },
            },
        });
    };

    return (
        <Box>
            {/* Material selector ------------------------------------------------ */}
            <ToggleButtonGroup value={selected} exclusive onChange={(_, val) => val && setSelected(val)} sx={{ mb: 2 }}>
                {materialEntries.map(([name, id]: any) => (
                    <ToggleButton key={id} value={id} sx={{ textTransform: 'none', p: '6px 16px' }}>
                        <Typography variant='body2' fontWeight={selected === id ? 700 : 500}>
                            {name}
                        </Typography>
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            {/* Label ------------------------------------------------------------- */}
            <Typography variant='body2' gutterBottom color='text.primary'>
                Offset (Default: {defaultVal})
            </Typography>

            {/* Slider ------------------------------------------------------------ */}
            <StyledSlider
                title={`${selected} Force Offset`}
                valueLabelDisplay={'on'}
                disabled={!unlocked}
                value={forceOffset}
                min={stepMap.min}
                max={stepMap.max}
                step={stepMap.step}
                onChange={handleSlider}
            />

            {/* Min / Max labels -------------------------------------------------- */}
            <Stack direction='row' justifyContent='space-between' mt={0.5}>
                <Typography variant='caption' color='text.primary'>
                    -{Math.abs(stepMap.min)}
                </Typography>
                <Typography variant='caption' color='text.primary'>
                    +{stepMap.max}
                </Typography>
            </Stack>
        </Box>
    );
};
